import { useField } from 'formik';
import {
  CompaniesPaginatedQuery,
  useCompaniesPaginatedQuery,
} from 'generated/graphql';
import { debounce } from 'lodash';
import uniqBy from 'lodash/uniqBy';
import { useState } from 'react';
import {
  FormikSelectFormField,
  IFormikSelectFormField,
  ISelectProps,
  Select,
  SelectFormField,
  SelectFormFieldProps,
  TNotification,
  TSelectOption,
  useNotification,
} from '@spotted-zebra-uk/ui-components';

export const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_TIME = 500;

type OptionsState = Record<string, TSelectOption<number>[]>;

type PaginationStateType = Record<
  string,
  { currentPage?: number; totalPages?: number }
>;

type UseGetCompaniesOptionsProps = {
  hasGlobal?: boolean;
  initialValue?: number;
};

const globalOption = { label: 'Global', value: -1 };

export function useGetCompaniesOptions({
  hasGlobal,
  initialValue,
}: UseGetCompaniesOptionsProps) {
  const { handleMsgType } = useNotification();
  const [options, setOptions] = useState<OptionsState>({
    '': [],
  });
  const [loading, setLoading] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationStateType>({
    '': {
      currentPage: 1,
      totalPages: undefined,
    },
  });
  const [searchValue, setSearchValue] = useState('');

  useCompaniesPaginatedQuery({
    variables: {
      filters: {
        name: searchValue,
      },
      pagination: {
        page: pagination[searchValue]?.currentPage
          ? pagination[searchValue].currentPage
          : 1,
        size: PAGE_SIZE,
      },
      includeCompanyIds: initialValue ? [initialValue] : undefined,
    },
    onCompleted: data => {
      const optionsFromResult = getOptionsFromResponse(data);
      const mergedOptions = mergePreviousAndNewOptions({
        hasGlobal,
        searchValue,
        prevOptions: options[searchValue] || [],
        newOptions: optionsFromResult,
      });
      const mergeInitialOptionsWithNewOptions = mergePreviousAndNewOptions({
        hasGlobal,
        searchValue: '',
        prevOptions: options[''] || [],
        newOptions: optionsFromResult,
      });

      setOptions(prevOptions => ({
        ...prevOptions,
        [searchValue]: mergedOptions,
        '': mergeInitialOptionsWithNewOptions,
      }));

      const pageInfo = data.CompaniesPaginated.pageInfo;
      setPagination(prevPagination => ({
        ...prevPagination,
        [searchValue]: {
          currentPage: pageInfo?.currentPage,
          totalPages: pageInfo?.pageTotal,
        },
      }));
      setLoading(false);
      setPaginationLoading(false);
    },
    onError: error => {
      handleMsgType({
        type: TNotification.error,
        message:
          error?.message || 'Oops, some issue happened while getting companies',
      });
    },
  });

  const search = async (inputValue: string) => {
    if (inputValue === searchValue) return;
    setLoading(true);
    setSearchValue(inputValue);
  };

  const debouncedSearch = debounce(search, SEARCH_DEBOUNCE_TIME);

  const loadMore = async () => {
    setPaginationLoading(true);
    const currentPage = pagination[searchValue]?.currentPage;
    const totalPages = pagination[searchValue]?.totalPages;
    const newPage = currentPage ? currentPage + 1 : 1;
    if (totalPages !== undefined && newPage > totalPages) {
      setPaginationLoading(false);
      return;
    }
    setPagination(prev => ({
      ...prev,
      [searchValue]: {
        currentPage: newPage,
        totalPages: pagination[searchValue]?.totalPages,
      },
    }));
  };

  return {
    options: options[searchValue] || [],
    loading: loading,
    currentPage: pagination[searchValue]?.currentPage,
    totalPages: pagination[searchValue]?.totalPages,
    debouncedSearch,
    paginationLoading,
    loadMore,
  };
}

function mergePreviousAndNewOptions({
  searchValue,
  hasGlobal,
  prevOptions,
  newOptions,
}: {
  searchValue: string;
  hasGlobal?: boolean;
  prevOptions: TSelectOption<number>[];
  newOptions: TSelectOption<number>[];
}) {
  return uniqBy(
    [
      ...(hasGlobal &&
      (globalOption.label.includes(searchValue) || searchValue === '')
        ? [globalOption]
        : []),
      ...prevOptions,
      ...newOptions,
    ],
    'value'
  );
}

function getOptionsFromResponse(
  companies: CompaniesPaginatedQuery
): TSelectOption<number>[] {
  return (
    companies.CompaniesPaginated.data?.map(company => ({
      label: company.name,
      value: company.id,
    })) || []
  );
}

export type CompanySelectPaginatedProps = Omit<
  ISelectProps<number>,
  | 'options'
  | 'onMenuScrollToBottom'
  | 'onInputChange'
  | 'isLoading'
  | 'isSearchable'
  | 'disableFilter'
  | 'loadingPaginatedOptions'
> & {
  hasGlobal?: boolean;
  onChange: (value: TSelectOption<string>, id: string) => void;
  initialValue?: number;
};

export function CompanySelectPaginated({
  initialValue,
  hasGlobal,
  ...props
}: CompanySelectPaginatedProps) {
  const { options, loading, debouncedSearch, paginationLoading, loadMore } =
    useGetCompaniesOptions({ hasGlobal, initialValue });

  return (
    <Select<number>
      options={options}
      onMenuScrollToBottom={loadMore}
      onInputChange={debouncedSearch}
      isLoading={loading}
      isSearchable
      disableFilter
      loadingPaginatedOptions={paginationLoading}
      {...props}
    />
  );
}

export type CompanySelectFormFieldPaginatedProps = Omit<
  SelectFormFieldProps<number>,
  | 'options'
  | 'onMenuScrollToBottom'
  | 'onInputChange'
  | 'isLoading'
  | 'isSearchable'
  | 'disableFilter'
  | 'loadingPaginatedOptions'
> & {
  hasGlobal?: boolean;
  onChange: (value: TSelectOption<number>, id: string) => void;
  initialValue?: number;
};

export function CompanySelectFormFieldPaginated({
  initialValue,
  hasGlobal,
  ...props
}: CompanySelectFormFieldPaginatedProps) {
  const { options, loading, debouncedSearch, paginationLoading, loadMore } =
    useGetCompaniesOptions({ hasGlobal, initialValue });

  return (
    <SelectFormField<number>
      options={options}
      onMenuScrollToBottom={loadMore}
      onInputChange={debouncedSearch}
      isLoading={loading}
      isSearchable
      disableFilter
      loadingPaginatedOptions={paginationLoading}
      {...props}
    />
  );
}

export type FormikCompanySelectFormFieldPaginatedProps = Omit<
  IFormikSelectFormField<number>,
  | 'options'
  | 'onMenuScrollToBottom'
  | 'onInputChange'
  | 'isLoading'
  | 'isSearchable'
  | 'disableFilter'
  | 'loadingPaginatedOptions'
  | 'useFormikField'
> & {
  hasGlobal?: boolean;
  onChange: (value: TSelectOption<number>, id: string) => void;
  initialValue?: number;
};

export function FormikCompanySelectFormFieldPaginated({
  initialValue,
  hasGlobal,
  ...props
}: CompanySelectFormFieldPaginatedProps) {
  const { options, loading, debouncedSearch, paginationLoading, loadMore } =
    useGetCompaniesOptions({ hasGlobal, initialValue });

  return (
    <FormikSelectFormField<number>
      options={options}
      onMenuScrollToBottom={loadMore}
      onInputChange={debouncedSearch}
      isLoading={loading}
      isSearchable
      disableFilter
      loadingPaginatedOptions={paginationLoading}
      useFormikField={useField}
      {...props}
    />
  );
}
